import React, { useState } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PAGE_SIZE = 20;

const sortMilestones = (milestones, showAll) =>
  [...milestones]
    .filter(m => m.totalUS > 0 && (showAll || m.remainingUS > 0))
    .sort((a, b) => {
      if (a.deadline && b.deadline) return new Date(a.deadline) - new Date(b.deadline);
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return 0;
    })
    .map(m => ({
      ...m,
      doneUS: m.totalUS - m.remainingUS,
      name: m.name.length > 20 ? m.name.slice(0, 20) + '…' : m.name,
      fullName: m.name,
    }));

const getLastDeadlinePage = (sortedData) => {
  let lastIdx = -1;
  sortedData.forEach((m, i) => { if (m.deadline) lastIdx = i; });
  return lastIdx >= 0 ? Math.floor(lastIdx / PAGE_SIZE) : 0;
};

const STATUS_COLOR = {
  overdue:    '#EF4444',
  inprogress: '#F59E0B',
  upcoming:   '#6B4C9A'
};

const STATUS_LABEL = {
  overdue:    'En retard',
  inprogress: 'Imminent (≤7j)',
  upcoming:   'À venir'
};

const DONE_COLOR = '#10B981';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 min-w-[210px]">
      <p className="font-semibold text-gray-800 text-xs mb-0.5 truncate">{d?.fullName}</p>
      <p className="text-xs text-gray-400 mb-2">{d?.partnerName || d?.projectName}</p>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between gap-4">
          <span className="font-medium text-emerald-600">US réalisées</span>
          <span className="font-bold">{d?.doneUS}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="font-medium text-purple-700">US restantes</span>
          <span className="font-bold">{d?.remainingUS} / {d?.totalUS}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">SP restants</span>
          <span>{d?.remainingSP} / {d?.totalSP}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-orange-500">Tickets ouverts</span>
          <span>{d?.openTickets} / {d?.totalTickets}</span>
        </div>
        {d?.deadline && (
          <div className="flex justify-between gap-4 pt-1 border-t border-gray-100">
            <span className="text-gray-400">Deadline</span>
            <span>{format(new Date(d.deadline), 'd MMM yyyy', { locale: fr })}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const CustomXTick = ({ x, y, payload, data }) => {
  const entry = data.find(d => d.name === payload.value);
  const isOverdue = entry?.status === 'overdue';
  const deadline = entry?.deadline
    ? format(new Date(entry.deadline), 'd MMM yy', { locale: fr })
    : '';

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        transform="rotate(-35)"
        textAnchor="end"
        fontSize={10}
        fontWeight={isOverdue ? 700 : 500}
        fill={isOverdue ? '#EF4444' : '#374151'}
        dy={4}
      >
        {payload.value}
      </text>
      {deadline && (
        <text
          transform="rotate(-35)"
          textAnchor="end"
          fontSize={9}
          fill={isOverdue ? '#FCA5A5' : '#9CA3AF'}
          dy={14}
        >
          {deadline}
        </text>
      )}
    </g>
  );
};

const MilestonesByTicketsChart = ({ milestones = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(() => getLastDeadlinePage(sortMilestones(milestones, false)));

  const allData = sortMilestones(milestones, showAll);

  const totalPages = Math.ceil(allData.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const data = allData.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  if (allData.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <h3 className="text-sm font-bold text-gray-900">Jalons actifs — Avancement US</h3>
        </div>
        <div className="flex items-center justify-center h-32 text-sm text-gray-400">
          Aucun jalon actif trouvé
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Jalons actifs — Avancement US</h3>
            <p className="text-xs text-gray-500">{data.length} jalon{data.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button
            onClick={() => {
              const next = !showAll;
              setShowAll(next);
              setPage(getLastDeadlinePage(sortMilestones(milestones, next)));
            }}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors ${
              showAll
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
            }`}
          >
            {showAll ? 'US restantes seult.' : 'Tous les jalons'}
          </button>
          <div className="flex gap-3 flex-wrap justify-end">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DONE_COLOR }} />
              Réalisées
            </div>
            {Object.entries(STATUS_LABEL).map(([key, label]) => (
              <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLOR[key] }} />
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 16, left: 0, bottom: 80 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={props => <CustomXTick {...props} data={data} />}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />

            {/* Barre 1 : US réalisées (vert) */}
            <Bar dataKey="doneUS" name="US réalisées" stackId="a" fill={DONE_COLOR} radius={[0, 0, 0, 0]} />

            {/* Barre 2 : US restantes (couleur statut) */}
            <Bar
              dataKey="remainingUS"
              name="US restantes"
              stackId="a"
              radius={[4, 4, 0, 0]}
              label={{
                content: ({ x, y, width, index }) => {
                  const entry = data[index];
                  if (!entry) return null;
                  return (
                    <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={10} fill="#6B7280">
                      {entry.doneUS}/{entry.totalUS}
                    </text>
                  );
                }
              }}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={STATUS_COLOR[entry.status] || '#6B4C9A'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={safePage === 0}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-500">
            Page <span className="font-semibold text-gray-800">{safePage + 1}</span> / {totalPages}
            <span className="ml-2 text-gray-400">({allData.length} jalons)</span>
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={safePage === totalPages - 1}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MilestonesByTicketsChart;
